import { useEffect, useState } from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const QUO_STATUS_URL = 'https://status.quo.com';
const QUO_STATUS_API_URL = `${QUO_STATUS_URL}/api/v2/status.json`;
const QUO_STATUS_REFRESH_MS = 5 * 60 * 1000;

const QUO_STATUS_META = {
  none: { color: '#2e7d32', labelKey: 'quo-status.operational' },
  minor: { color: '#ed6c02', labelKey: 'quo-status.partial-degradation' },
  major: { color: '#d32f2f', labelKey: 'quo-status.major-outage' },
  critical: { color: '#b71c1c', labelKey: 'quo-status.critical-outage' },
  maintenance: { color: '#0288d1', labelKey: 'quo-status.maintenance' },
  unknown: { color: '#9e9e9e', labelKey: 'quo-status.unavailable' },
};

/**
 * Compact Quo (OpenPhone) status for the admin sidebar footer.
 */
export default function QuoStatusBox() {
  const { t } = useTranslation();
  // `description` is text from the Quo API; `descriptionKey` is our own fallback copy.
  const [status, setStatus] = useState({
    descriptionKey: 'quo-status.checking',
    indicator: 'unknown',
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;

    const fetchStatus = async () => {
      if (typeof fetch !== 'function') {
        setStatus({
          descriptionKey: 'quo-status.unable-to-check',
          indicator: 'unknown',
          loading: false,
        });
        return;
      }

      try {
        const response = await fetch(QUO_STATUS_API_URL, {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          throw new Error('Unable to fetch Quo status');
        }

        const data = await response.json();
        if (cancelled) return;

        setStatus({
          description: data?.status?.description,
          descriptionKey: QUO_STATUS_META.unknown.labelKey,
          indicator: data?.status?.indicator || 'unknown',
          loading: false,
        });
      } catch {
        if (cancelled) return;

        setStatus({
          descriptionKey: 'quo-status.unable-to-check',
          indicator: 'unknown',
          loading: false,
        });
      }
    };

    fetchStatus();
    const interval = window.setInterval(fetchStatus, QUO_STATUS_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const meta = QUO_STATUS_META[status.indicator] || QUO_STATUS_META.unknown;
  const description = status.description || t(status.descriptionKey);
  const label = status.loading ? description : `Quo: ${description}`;

  return (
    <Tooltip title={label}>
      <Box
        component={Link}
        href={QUO_STATUS_URL}
        target="_blank"
        rel="noreferrer"
        underline="none"
        aria-label={label}
        sx={{
          mx: 2,
          mb: 1,
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderRadius: 1,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: (theme) => theme.palette.background.neutral,
          color: 'text.secondary',
          textDecoration: 'none',
          '&:hover': {
            bgcolor: (theme) => theme.palette.action.hover,
            color: 'text.primary',
          },
        }}
      >
        <Box
          component="span"
          sx={{
            width: 10,
            height: 10,
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: meta.color,
          }}
        />
        <Typography variant="caption" noWrap sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}
